import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  AlertTriangle, 
  CheckCircle2, 
  RefreshCw, 
  Zap,
  FileWarning,
  Volume2,
  Play,
  Loader2,
  RotateCcw,
  Sparkles
} from 'lucide-react';
import { useContentQuality } from '@/hooks/useContentQuality';
import { useToast } from '@/hooks/use-toast';
import { FixProgress } from '@/services/contentFixService';

export const ContentFixPanel: React.FC = () => {
  const { toast } = useToast();
  const {
    report,
    isAnalyzing,
    isFixing,
    lastAnalyzed,
    errors,
    fixResult,
    canManageContent,
    analyzeContentQuality,
    fixCorruptedContent,
    fixMissingAudio
  } = useContentQuality();

  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [progress, setProgress] = useState<FixProgress | null>(null);
  const [enableAutoRetry, setEnableAutoRetry] = useState(true);

  useEffect(() => {
    if (canManageContent) {
      analyzeContentQuality();
    }
  }, [canManageContent]);

  const handleProgress = useCallback((p: FixProgress) => {
    setProgress(p);
  }, []);

  const handleFixAll = async () => {
    setProgress({
      operationId: 'starting',
      phase: 'identifying',
      processed: 0,
      total: report?.corruptedCount || 0,
      successful: 0,
      failed: 0,
      currentBatch: 0,
      totalBatches: 0,
    });

    const result = await fixCorruptedContent(undefined, handleProgress, enableAutoRetry);
    
    if (result) {
      toast({
        title: 'Content Regeneration Complete',
        description: `${result.fixed} modules fixed, ${result.failed} failed.`,
      });
      // Re-analyze after fix
      await analyzeContentQuality();
    }
    setProgress(null);
  };

  const handleFixSelected = async () => {
    if (selectedModules.length === 0) {
      toast({
        title: 'No modules selected',
        description: 'Please select modules to fix.',
        variant: 'destructive'
      });
      return;
    }

    setProgress({
      operationId: 'starting',
      phase: 'regenerating',
      processed: 0,
      total: selectedModules.length,
      successful: 0,
      failed: 0,
      currentBatch: 0,
      totalBatches: Math.ceil(selectedModules.length / 10),
    });

    const result = await fixCorruptedContent(selectedModules, handleProgress, enableAutoRetry);
    
    if (result) {
      toast({
        title: 'Content Regeneration Complete',
        description: `${result.fixed} modules fixed, ${result.failed} failed.`,
      });
      setSelectedModules([]);
      await analyzeContentQuality();
    }
    setProgress(null);
  };

  const handleFixAudio = async () => {
    const result = await fixMissingAudio();
    if (result?.success) {
      toast({
        title: 'Audio Generation Started',
        description: `Generating audio for ${result.processed} modules.`,
      });
    }
  };

  const toggleModule = (moduleId: string) => {
    setSelectedModules(prev => 
      prev.includes(moduleId) 
        ? prev.filter(id => id !== moduleId)
        : [...prev, moduleId]
    );
  };

  const getPhaseLabel = (phase: string) => {
    switch (phase) {
      case 'identifying': return 'Identifying corrupted modules...';
      case 'regenerating': return 'Regenerating content with Lovable AI...';
      case 'retrying': return 'Auto-retrying failed modules...';
      case 'audio': return 'Generating audio...';
      case 'complete': return 'Complete!';
      default: return 'Processing...';
    }
  };

  if (!canManageContent) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          You need admin or staff permissions to access content fixes.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Panel */}
      {progress && (
        <Card className="border-primary bg-primary/5">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              {progress.phase === 'retrying' ? (
                <RotateCcw className="h-5 w-5 animate-spin text-yellow-500" />
              ) : progress.phase === 'complete' ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              )}
              {getPhaseLabel(progress.phase)}
            </CardTitle>
            {progress.retryAttempt && (
              <CardDescription className="text-yellow-600">
                Retry attempt {progress.retryAttempt} of {progress.maxRetries}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress: {progress.processed} / {progress.total}</span>
                <span>{progress.total > 0 ? Math.round((progress.processed / progress.total) * 100) : 0}%</span>
              </div>
              <Progress 
                value={progress.total > 0 ? (progress.processed / progress.total) * 100 : 0} 
              />
            </div>
            
            {progress.totalBatches > 0 && (
              <div className="text-sm text-muted-foreground">
                Batch {progress.currentBatch} of {progress.totalBatches}
              </div>
            )}

            <div className="flex gap-4 text-sm">
              <span className="text-green-500 flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4" />
                {progress.successful} successful
              </span>
              <span className="text-destructive flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                {progress.failed} failed
              </span>
            </div>

            {progress.phase === 'retrying' && (
              <Alert className="bg-yellow-50 border-yellow-200">
                <RotateCcw className="h-4 w-4 text-yellow-600" />
                <AlertDescription className="text-yellow-800">
                  Self-healing in progress: automatically retrying {progress.failed} failed modules...
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Content Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {report?.overallScore || 0}%
            </div>
            <Progress value={report?.overallScore || 0} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <FileWarning className="h-4 w-4 text-destructive" />
              Corrupted Modules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-destructive">
              {report?.corruptedCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              of {report?.totalModules || 0} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Volume2 className="h-4 w-4 text-yellow-500" />
              Missing Audio
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-500">
              {report?.missingAudioCount || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              modules need audio
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              Healthy Modules
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">
              {report?.healthyModules || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              content verified
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            Self-Healing Content Fix
          </CardTitle>
          <CardDescription>
            Powered by Lovable AI (google/gemini-2.5-flash) with automatic retry for failed modules
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Auto-retry toggle */}
          <div className="flex items-center space-x-2 p-3 bg-muted/50 rounded-lg">
            <Switch
              id="auto-retry"
              checked={enableAutoRetry}
              onCheckedChange={setEnableAutoRetry}
            />
            <Label htmlFor="auto-retry" className="flex items-center gap-2">
              <RotateCcw className="h-4 w-4" />
              Enable Self-Healing Auto-Retry
              <Badge variant="secondary" className="ml-2">
                Up to 3 retries
              </Badge>
            </Label>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => analyzeContentQuality()}
              disabled={isAnalyzing || !!progress}
              variant="outline"
            >
              {isAnalyzing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Re-analyze Content
            </Button>

            <Button
              onClick={handleFixAll}
              disabled={isFixing || !report?.corruptedCount || !!progress}
              variant="default"
              className="bg-gradient-to-r from-primary to-primary/80"
            >
              {progress?.phase === 'regenerating' || progress?.phase === 'retrying' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Fix All Corrupted ({report?.corruptedCount || 0})
            </Button>

            <Button
              onClick={handleFixAudio}
              disabled={isFixing || !report?.missingAudioCount || !!progress}
              variant="secondary"
            >
              {isFixing ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Volume2 className="h-4 w-4 mr-2" />
              )}
              Generate Missing Audio ({report?.missingAudioCount || 0})
            </Button>

            {selectedModules.length > 0 && (
              <Button
                onClick={handleFixSelected}
                disabled={isFixing || !!progress}
                variant="outline"
              >
                Fix Selected ({selectedModules.length})
              </Button>
            )}
          </div>

          {lastAnalyzed && (
            <p className="text-xs text-muted-foreground">
              Last analyzed: {lastAnalyzed.toLocaleString()}
            </p>
          )}

          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {errors.join(', ')}
              </AlertDescription>
            </Alert>
          )}

          {fixResult && !progress && (
            <Alert className={fixResult.failed === 0 ? 'border-green-500 bg-green-50' : ''}>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Last fix: {fixResult.fixed} fixed, {fixResult.failed} failed of {fixResult.totalCorrupted} total
                {fixResult.failed === 0 && ' ✨ Perfect!'}
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Corrupted Modules List */}
      {report?.corruptedModules && report.corruptedModules.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileWarning className="h-5 w-5 text-destructive" />
              Corrupted Modules ({report.corruptedModules.length})
            </CardTitle>
            <CardDescription>
              Click to select modules for targeted regeneration
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <div className="space-y-2">
                {report.corruptedModules.map((module) => (
                  <div
                    key={module.moduleId}
                    onClick={() => toggleModule(module.moduleId)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedModules.includes(module.moduleId)
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {module.courseId}
                          </Badge>
                          <span className="font-mono text-sm">
                            {module.moduleId}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {module.issue}
                        </p>
                      </div>
                      {selectedModules.includes(module.moduleId) && (
                        <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContentFixPanel;
